import { useState, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { sendAIMessage, getAIChatHistory, getAISessions, deleteAISession } from "@/lib/api";
import { 
  MessageCircle, X, Send, Loader2, Plane, Trash2, 
  Plus, ChevronLeft, Bot, User, Sparkles 
} from "lucide-react";
import { toast } from "sonner";

export default function AIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Load sessions when opening session list
  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const response = await getAISessions();
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error("Failed to load sessions:", error);
    } finally {
      setLoadingSessions(false);
    }
  };

  // Load chat history for a session
  const loadChatHistory = async (sid) => {
    try {
      const response = await getAIChatHistory(sid);
      const history = response.data.history || [];
      setMessages(history.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      })));
      setSessionId(sid);
      setShowSessions(false);
    } catch (error) {
      console.error("Failed to load chat history:", error);
      toast.error("Failed to load chat history");
    }
  };

  // Start new chat
  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowSessions(false);
  };

  // Delete a session
  const handleDeleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await deleteAISession(sid);
      setSessions(sessions.filter(s => s.session_id !== sid));
      if (sessionId === sid) {
        startNewChat();
      }
      toast.success("Chat deleted");
    } catch (error) {
      toast.error("Failed to delete chat");
    }
  };

  // Send message
  const handleSend = async () => {
    if (!message.trim() || loading) return;

    const userMessage = message.trim();
    setMessage("");
    
    // Add user message to UI immediately
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await sendAIMessage(userMessage, sessionId);
      const data = response.data;
      
      // Update session ID if new
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
      }
      
      // Add AI response
      setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
    } catch (error) {
      console.error("AI error:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I apologize, but I encountered an error. Please try again.",
        error: true
      }]);
      toast.error("Failed to get AI response");
    } finally {
      setLoading(false);
    }
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Format message content with markdown-like formatting
  const formatContent = (content) => {
    // Split by code blocks first
    const parts = content.split(/(```[\s\S]*?```)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith("```")) {
        // Code block
        const code = part.replace(/```\w*\n?/g, "").replace(/```$/g, "");
        return (
          <pre key={index} className="bg-[#1e293b] text-green-400 p-2 rounded text-xs overflow-x-auto my-2">
            <code>{code}</code>
          </pre>
        );
      }
      
      // Process regular text
      return (
        <span key={index}>
          {part.split("\n").map((line, i) => {
            // Bold text
            line = line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
            // Bullet points
            if (line.trim().startsWith("- ") || line.trim().startsWith("• ")) {
              return (
                <div key={i} className="flex gap-2 ml-2">
                  <span>•</span>
                  <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(line.replace(/^[-•]\s*/, "")) }} />
                </div>
              );
            }
            // Numbered lists
            if (/^\d+\.\s/.test(line.trim())) {
              return (
                <div key={i} className="ml-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(line) }} />
              );
            }
            return line ? <p key={i} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(line) }} /> : <br key={i} />;
          })}
        </span>
      );
    });
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen && sessions.length === 0) {
            loadSessions();
          }
        }}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? "bg-[#0f172a] hover:bg-[#1e293b] rotate-0" 
            : "bg-gradient-to-r from-[#d4af37] to-[#f4cf57] hover:from-[#b5952f] hover:to-[#d4af37]"
        }`}
        data-testid="ai-assistant-btn"
      >
        {isOpen ? (
          <X size={24} className="text-white" />
        ) : (
          <div className="relative">
            <Bot size={26} className="text-[#0f172a]" />
            <Sparkles size={12} className="absolute -top-1 -right-1 text-[#0f172a]" />
          </div>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-50 w-[380px] h-[520px] bg-white dark:bg-[#0f172a] rounded-2xl shadow-2xl border border-[#e2e8f0] dark:border-[#334155] flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-300"
          data-testid="ai-assistant-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#0f172a] to-[#1e293b] p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {showSessions && (
                <button 
                  onClick={() => setShowSessions(false)}
                  className="text-white/70 hover:text-white"
                >
                  <ChevronLeft size={20} />
                </button>
              )}
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#d4af37] to-[#f4cf57] flex items-center justify-center">
                <Plane size={20} className="text-[#0f172a]" />
              </div>
              <div>
                <h3 className="text-white font-semibold text-sm">Aviation Assistant</h3>
                <p className="text-white/60 text-xs">Live Data • GPT-5.2</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setShowSessions(!showSessions);
                  if (!showSessions) loadSessions();
                }}
                className="text-white/70 hover:text-white p-1"
                title="Chat History"
              >
                <MessageCircle size={18} />
              </button>
              <button
                onClick={startNewChat}
                className="text-white/70 hover:text-white p-1"
                title="New Chat"
              >
                <Plus size={18} />
              </button>
            </div>
          </div>

          {/* Sessions List */}
          {showSessions ? (
            <div className="flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="p-3 space-y-2">
                  {loadingSessions ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-[#d4af37]" />
                    </div>
                  ) : sessions.length === 0 ? (
                    <div className="text-center py-8 text-[#64748b] dark:text-gray-400">
                      <MessageCircle size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No previous chats</p>
                    </div>
                  ) : (
                    sessions.map((session) => (
                      <div
                        key={session.session_id}
                        onClick={() => loadChatHistory(session.session_id)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors flex items-start justify-between group ${
                          sessionId === session.session_id
                            ? "bg-[#d4af37]/20 border border-[#d4af37]"
                            : "bg-[#f8fafc] dark:bg-[#1e293b] hover:bg-[#f1f5f9] dark:hover:bg-[#334155]"
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#0f172a] dark:text-white truncate">
                            {session.last_message}
                          </p>
                          <p className="text-xs text-[#64748b] dark:text-gray-400 mt-1">
                            {new Date(session.last_timestamp).toLocaleDateString()} · {session.message_count} messages
                          </p>
                        </div>
                        <button
                          onClick={(e) => handleDeleteSession(session.session_id, e)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:text-red-600 transition-opacity"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          ) : (
            <>
              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                {messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center px-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-[#d4af37]/20 to-[#f4cf57]/20 flex items-center justify-center mb-4">
                      <Plane size={32} className="text-[#d4af37]" />
                    </div>
                    <h4 className="font-semibold text-[#0f172a] dark:text-white mb-2">
                      Aviation Expert at Your Service
                    </h4>
                    <p className="text-sm text-[#64748b] dark:text-gray-400 mb-4">
                      I have access to live flight data, aircraft info, and operational statistics. Ask me anything!
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {[
                        "What flights are scheduled today?",
                        "Any non-Schengen arrivals?",
                        "Show today's service requests",
                        "Traffic statistics"
                      ].map((suggestion) => (
                        <button
                          key={suggestion}
                          onClick={() => setMessage(suggestion)}
                          className="text-xs px-3 py-1.5 rounded-full bg-[#f8fafc] dark:bg-[#1e293b] text-[#64748b] dark:text-gray-400 hover:bg-[#d4af37]/20 hover:text-[#d4af37] transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg, index) => (
                      <div
                        key={index}
                        className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                          msg.role === "user" 
                            ? "bg-[#0f172a]" 
                            : "bg-gradient-to-r from-[#d4af37] to-[#f4cf57]"
                        }`}>
                          {msg.role === "user" ? (
                            <User size={16} className="text-white" />
                          ) : (
                            <Bot size={16} className="text-[#0f172a]" />
                          )}
                        </div>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                          msg.role === "user"
                            ? "bg-[#0f172a] text-white rounded-tr-sm"
                            : msg.error
                              ? "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-tl-sm"
                              : "bg-[#f8fafc] dark:bg-[#1e293b] text-[#0f172a] dark:text-white rounded-tl-sm"
                        }`}>
                          <div className="text-sm leading-relaxed">
                            {msg.role === "assistant" ? formatContent(msg.content) : msg.content}
                          </div>
                        </div>
                      </div>
                    ))}
                    {loading && (
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#d4af37] to-[#f4cf57] flex items-center justify-center">
                          <Bot size={16} className="text-[#0f172a]" />
                        </div>
                        <div className="bg-[#f8fafc] dark:bg-[#1e293b] rounded-2xl rounded-tl-sm px-4 py-3">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-[#d4af37] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                            <div className="w-2 h-2 bg-[#d4af37] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                            <div className="w-2 h-2 bg-[#d4af37] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Input */}
              <div className="p-4 border-t border-[#e2e8f0] dark:border-[#334155]">
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about aviation..."
                    disabled={loading}
                    className="flex-1 bg-[#f8fafc] dark:bg-[#1e293b] border-[#e2e8f0] dark:border-[#334155] rounded-full px-4"
                    data-testid="ai-message-input"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={!message.trim() || loading}
                    className="rounded-full w-10 h-10 p-0 bg-gradient-to-r from-[#d4af37] to-[#f4cf57] hover:from-[#b5952f] hover:to-[#d4af37] text-[#0f172a]"
                    data-testid="ai-send-btn"
                  >
                    {loading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Send size={18} />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
